/**
 * Torpedo lock (spec §6): the selected target must stay within the torpedo's lock range and cone of the nose
 * for its lock time. Leaving the cone or range resets the lock. Float64 relative vectors (D-011),
 * allocation-free (D-014).
 */
import type { ShipDef, TorpedoWeaponDef, WeaponsData } from '../data/schemas'
import { Life, Lock, Pos64, Rot, Ship, Target } from '../world/components'
import { emitEvent, EV, type EventRing } from './events'
import { isLivingEnemy, type ShipList } from './targeting'

/** f32 accumulation slack: 120 × (1/60) summed in float32 lands a hair under 2 s. */
const HELD_EPS = 1e-4

/** The torpedo definition in the weapons data (the first `kind: 'torpedo'` weapon), or null. */
export function torpedoWeapon(weapons: WeaponsData): TorpedoWeaponDef | null {
  const list = weapons.weapons
  for (let i = 0; i < list.length; i++) {
    const w = list[i]
    if (w.kind === 'torpedo') return w
  }
  return null
}

/** True when the ship def carries a secondary (torpedo) mount. */
export function hasSecondaryMount(def: ShipDef): boolean {
  const mounts = def.mounts
  for (let i = 0; i < mounts.length; i++) if (mounts[i].group === 'secondary') return true
  return false
}

function emitLock(events: EventRing, type: typeof EV.lockStart | typeof EV.locked | typeof EV.lockLost, eid: number, target: number): void {
  // Position = the shooter (so distance-based consumers such as audio treat it as the shooter's own sound).
  emitEvent(events, type, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0, 0, 0, eid, target, 0, 0)
}

function reset(eid: number, events: EventRing): void {
  if (Lock.held[eid] > 0 || Lock.locked[eid] === 1) emitLock(events, EV.lockLost, eid, Lock.target[eid])
  Lock.held[eid] = 0
  Lock.locked[eid] = 0
}

/** True when `t` is within `range` metres and `cone` radians of `eid`'s nose (local −Z). */
function inLockCone(eid: number, t: number, range: number, cone: number): boolean {
  const rx = Pos64.x[t] - Pos64.x[eid]
  const ry = Pos64.y[t] - Pos64.y[eid]
  const rz = Pos64.z[t] - Pos64.z[eid]
  const d2 = rx * rx + ry * ry + rz * rz
  if (d2 > range * range) return false
  if (d2 === 0) return true
  const qx = Rot.x[eid]
  const qy = Rot.y[eid]
  const qz = Rot.z[eid]
  const qw = Rot.w[eid]
  const nx = -2 * (qx * qz + qw * qy)
  const ny = -2 * (qy * qz - qw * qx)
  const nz = -(1 - 2 * (qx * qx + qy * qy))
  return rx * nx + ry * ny + rz * nz >= Math.cos(cone) * Math.sqrt(d2)
}

/**
 * One lock step for ship `eid` against its `Target`. While the target is a living enemy inside the torpedo's
 * lock range and cone, `Lock.held` grows by `dt` (`lockStart` when it leaves 0; `locked` and `Lock.locked = 1`
 * when it reaches the lock time). Leaving the cone or range, a target change, a dead target or a dead shooter
 * resets `held` and `locked` (`lockLost` if the lock was building or complete). Events: eid = shooter,
 * other = target, x/y/z = shooter position.
 */
export function stepLock(eid: number, dt: number, weapons: WeaponsData, events: EventRing): void {
  const torpedo = torpedoWeapon(weapons)
  const target = Target.eid[eid]
  if (target !== Lock.target[eid]) {
    reset(eid, events)
    Lock.target[eid] = target
  }
  if (torpedo === null || Life.alive[eid] !== 1 || !isLivingEnemy(eid, target) || !inLockCone(eid, target, torpedo.lock.range, torpedo.lock.cone)) {
    reset(eid, events)
    return
  }
  if (Lock.locked[eid] === 1) return
  if (Lock.held[eid] === 0) emitLock(events, EV.lockStart, eid, target)
  const held = Lock.held[eid] + dt
  if (held >= torpedo.lock.seconds - HELD_EPS) {
    Lock.held[eid] = torpedo.lock.seconds
    Lock.locked[eid] = 1
    emitLock(events, EV.locked, eid, target)
  } else {
    Lock.held[eid] = held
  }
}

/** `stepLock` for every ship whose def carries a torpedo mount (AI raiders without torpedoes never build a lock). */
export function stepLocks(ships: ShipList, defs: readonly ShipDef[], weapons: WeaponsData, events: EventRing, dt: number): void {
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    if (hasSecondaryMount(defs[Ship.defIndex[eid]])) stepLock(eid, dt, weapons, events)
  }
}

/** Lock progress 0..1 for the HUD ring and the lock tone (1 once locked). */
export function lockProgress(eid: number, weapons: WeaponsData): number {
  if (Lock.locked[eid] === 1) return 1
  const torpedo = torpedoWeapon(weapons)
  if (torpedo === null) return 0
  const p = Lock.held[eid] / torpedo.lock.seconds
  return p < 0 ? 0 : p > 1 ? 1 : p
}
